import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Settings } from 'lucide-react';
import { WidgetConfig, ReportData, ReportTheme } from '../types';
import WidgetRenderer from './widgets/WidgetRenderer';

interface ReportDisplayProps {
  reportData: ReportData;
  reportTheme: ReportTheme;
  widgets: WidgetConfig[];
  onDragStart: (e: React.DragEvent, widgetId: string) => void;
  onDragOver: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent, targetWidgetId: string) => void;
  onDeleteWidget: (widgetId: string) => void;
  onCustomizeClick?: () => void;
  targetRef: React.RefObject<HTMLDivElement>;
}

const ReportDisplay: React.FC<ReportDisplayProps> = ({
  reportData,
  reportTheme,
  widgets,
  onDragStart,
  onDragOver,
  onDrop,
  onDeleteWidget,
  onCustomizeClick,
  targetRef
}) => {
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Report Layout</CardTitle>
            <CardDescription>
              Drag widgets to rearrange them. Click the trash icon to remove widgets.
            </CardDescription>
          </div>
          {onCustomizeClick && (
            <Button
              variant="outline"
              size="sm"
              onClick={onCustomizeClick}
              className="flex items-center gap-2"
            >
              <Settings className="h-4 w-4" />
              Customize
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <div 
          ref={targetRef} 
          className={`min-h-[800px] border-2 border-dashed border-muted-foreground/25 rounded-lg p-4 ${
            reportTheme.theme === 'light' 
              ? 'bg-white text-gray-900' 
              : 'bg-background text-foreground'
          }`}
        >
          {/* Report Header */}
          {reportData.reportSetup && (
            <div className={`mb-8 p-6 rounded-lg border ${
              reportTheme.theme === 'light' 
                ? 'bg-gray-50 border-gray-200' 
                : 'bg-muted/20 border-border'
            }`}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h2 className={`text-2xl font-bold mb-2 ${
                    reportTheme.theme === 'light' ? 'text-gray-900' : 'text-foreground'
                  }`}>Analytics Report</h2>
                  <div className={`space-y-2 ${
                    reportTheme.theme === 'light' ? 'text-gray-700' : 'text-foreground'
                  }`}>
                    <div>
                      <span className="font-semibold">Client:</span> {reportData.reportSetup.clientName}
                    </div>
                    <div>
                      <span className="font-semibold">Website:</span> {reportData.reportSetup.clientWebsite}
                    </div>
                    <div>
                      <span className="font-semibold">Report Period:</span> {
                        reportData.dateRange === 'custom' 
                          ? `${reportData.startDate} to ${reportData.endDate}`
                          : reportData.dateRange === 'month'
                          ? reportData.selectedMonth
                          : reportData.dateRange
                      }
                    </div>
                  </div>
                </div>
                <div>
                  <div className={`space-y-2 ${
                    reportTheme.theme === 'light' ? 'text-gray-700' : 'text-foreground'
                  }`}>
                    <div>
                      <span className="font-semibold">Prepared by:</span> {reportData.reportSetup.companyName}
                    </div>
                    {reportData.reportSetup.companyAddress && (
                      <div>
                        <span className="font-semibold">Address:</span> {reportData.reportSetup.companyAddress}
                      </div>
                    )}
                    {reportData.reportSetup.companySubtext && (
                      <div>
                        <span className="font-semibold">Tagline:</span> {reportData.reportSetup.companySubtext}
                      </div>
                    )}
                    <div>
                      <span className="font-semibold">Generated:</span> {new Date().toLocaleDateString()}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {widgets.length === 0 ? (
            <div className="flex items-center justify-center h-full">
              <div className={`text-center ${
                reportTheme.theme === 'light' ? 'text-gray-500' : 'text-muted-foreground'
              }`}>
                <p className="text-lg mb-2">No widgets available</p>
                <p className="text-sm">Widgets will be loaded from the backend. Please check your configuration.</p>
              </div>
            </div>
          ) : (
            <div className="flex flex-wrap gap-4">
              {widgets
                .sort((a, b) => a.sortOrder - b.sortOrder)
                .map(widget => (
                  <WidgetRenderer
                    key={widget.id}
                    widget={widget}
                    reportTheme={reportTheme}
                    onDragStart={onDragStart}
                    onDragOver={onDragOver}
                    onDrop={onDrop}
                    onDeleteWidget={onDeleteWidget}
                  />
                ))
              }
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default ReportDisplay;
